import { Heading, Link as CLink, List, Stack, Text } from '@chakra-ui/react'
import { useRecipients } from '~/api/hooks'
import { ErrorAlert, useFriendlyError } from '~/components/member/feedback'
import { INLINE_TARGET, MemberLayout, PageTitle, TextLink } from '~/components/member/layout'
import { Rich, useT } from '~/i18n/use-t'

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Stack gap={3} as='section'>
    <Heading as='h2' size='xl'>
      {title}
    </Heading>
    {children}
  </Stack>
)

function RecipientList({ items: rows }: { items: Array<{ legalName: string; purpose: string; region?: string }> }) {
  const { t } = useT()
  return (
    <List.Root ps={6} gap={2}>
      {rows.map((r) => (
        <List.Item key={`${r.legalName}|${r.purpose}`}>
          <b>{r.legalName}</b>: {r.purpose}
          {r.region ? ` (${t('privacy.recipientRegion', { region: r.region })})` : ''}
        </List.Item>
      ))}
    </List.Root>
  )
}

/** One titled paragraph (or list) of the identity-document section. */
function DocPart({ part, list: asList }: { part: string; list?: boolean }) {
  const { t, list } = useT()
  return (
    <Stack gap={1}>
      <Heading as='h3' size='lg'>
        {t(`privacy.doc.${part}Title`)}
      </Heading>
      {asList ? (
        <List.Root ps={6} gap={2}>
          {list(`privacy.doc.${part}`).map((x) => (
            <List.Item key={x}>{x}</List.Item>
          ))}
        </List.Root>
      ) : (
        <Text>{t(`privacy.doc.${part}`)}</Text>
      )}
    </Stack>
  )
}

const PROCESSOR_CONTACT = 'info@synergize.dev'

/** /privacy: controller, processor, purposes and bases, recipients that follow actual processing (P4), the identity document (Task 33), retention. */
export default function Privacy() {
  const { t, list, config } = useT()
  // The organisation's contact may be the processor's own address: name it once.
  const sameContact = config.org.contact.trim().toLowerCase() === PROCESSOR_CONTACT
  const friendly = useFriendlyError()
  const rec = useRecipients()
  return (
    <MemberLayout backTo='/join' backHistory>
      <PageTitle title={t('privacy.title')} subtitle={t('privacy.updatedNote')} />
      <Section title={t('privacy.controllerTitle')}>
        <Text>{t('privacy.controller')}</Text>
      </Section>
      <Section title={t('privacy.processorTitle')}>
        <Text>{t('privacy.processor')}</Text>
      </Section>
      <Section title={t('privacy.purposesTitle')}>
        <List.Root ps={6} gap={2}>
          {list('privacy.purposes').map((p) => (
            <List.Item key={p}>{p}</List.Item>
          ))}
        </List.Root>
      </Section>
      <Section title={t('privacy.recipientsTitle')}>
        <Text>{t('privacy.recipientsIntro')}</Text>
        {rec.isError && <ErrorAlert>{friendly(rec.error)}</ErrorAlert>}
        {rec.data && (
          <>
            <Heading as='h3' size='lg'>
              {t('privacy.recipientsNow')}
            </Heading>
            {rec.data.current.length ? (
              <RecipientList items={rec.data.current} />
            ) : (
              <Text>{t('privacy.recipientsNone')}</Text>
            )}
            <Heading as='h3' size='lg'>
              {t('privacy.recipientsPast')}
            </Heading>
            {rec.data.past.length ? (
              <RecipientList items={rec.data.past} />
            ) : (
              <Text>{t('privacy.recipientsNone')}</Text>
            )}
            <Heading as='h3' size='lg'>
              {t('privacy.processorsTitle')}
            </Heading>
            <RecipientList items={rec.data.processors} />
            <Heading as='h3' size='lg'>
              {t('privacy.sourcesTitle')}
            </Heading>
            <List.Root ps={6} gap={2}>
              {rec.data.dataSources.map((s) => (
                <List.Item key={s.name}>
                  <b>{s.name}</b>: {s.purpose}
                </List.Item>
              ))}
            </List.Root>
          </>
        )}
      </Section>
      <Section title={t('privacy.doc.title')}>
        <Text>{t('privacy.doc.intro')}</Text>
        <DocPart part='basis' />
        <DocPart part='read' />
        <DocPart part='kept' list />
        <DocPart part='never' />
        <DocPart part='working' />
        <DocPart part='evidence' />
        <DocPart part='deletion' />
        <DocPart part='manual' />
        <DocPart part='withdraw' />
        <DocPart part='person' />
        <DocPart part='exempt' />
      </Section>
      <Section title={t('privacy.reviewTitle')}>
        <Text>{t('privacy.review')}</Text>
      </Section>
      <Section title={t('privacy.retentionTitle')}>
        <List.Root ps={6} gap={2}>
          {list('privacy.retention').map((r) => (
            <List.Item key={r}>{r}</List.Item>
          ))}
        </List.Root>
      </Section>
      <Section title={t('privacy.deletionTitle')}>
        <Text>{t('privacy.deletion')}</Text>
      </Section>
      <Section title={t('privacy.rightsTitle')}>
        <Text>{t('privacy.rights')}</Text>
      </Section>
      <Section title={t('privacy.contactTitle')}>
        <Text>{t(sameContact ? 'privacy.contactSame' : 'privacy.contact')}</Text>
      </Section>
      {/* R4: the Cookies page and the photo credit live here, not in the footer. */}
      <Section title={t('privacy.creditsTitle')}>
        <Text>
          <Rich k='privacy.cookiesLink' components={{ a: <TextLink to='/cookies'>{''}</TextLink> }} />
        </Text>
        <Text>
          <Rich
            k='privacy.credits'
            components={{
              a: (
                <CLink
                  href='https://unsplash.com'
                  target='_blank'
                  rel='noopener noreferrer'
                  textDecoration='underline'
                  css={INLINE_TARGET}
                />
              ),
            }}
          />
        </Text>
      </Section>
    </MemberLayout>
  )
}
